import { CustomAttributeValue } from './customAttribute';
import {
  ResponseStatus,
  RfiCategory,
  RfiCostImpact,
  RfiDiscipline,
  RfiPriority,
  RfiScheduleImpact,
  RfiStatus,
  WorkflowType,
} from './enums';
import { OxygenId, Assignee, AssigneeType } from './system';
import { AssigneeResponse } from './assigneeResponse';
import { Attachment } from './attachment';
/**
 *
 * @export
 * @interface BaseRfi
 */
export interface BaseRfi {
  id: string;
  customIdentifier?: string;
  title: string;
  question?: string | null;
  virtualFolderUrn?: string | null;
  status: RfiStatus;
  assignedTo: Assignee[];
  managerId?: OxygenId;
  constructionManagerId?: OxygenId;
  architects?: Assignee[] | null;
  reviewers?: Assignee[] | null;
  dueDate?: string | null;
  locationDescription?: string;
  locations?: RfiLocationId[];
  attachmentsCount?: number;
  commentsCount?: number;
  officialResponse?: string;
  officialResponseStatus?: ResponseStatus;
  respondedAt?: string;
  respondedBy?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  /** When the RFI most recently entered review. Omitted while it has never been in review. */
  openedAt?: string;
  closedAt?: string;
  closedBy?: string;
  containerId?: string;
  projectId: string;
  suggestedAnswer?: string;
  coReviewers?: Assignee[];
  optionalReviewers?: Assignee[];
  watchers?: Assignee[];
  answeredAt?: string;
  answeredBy?: string;
  costImpact?: RfiCostImpact | null;
  scheduleImpact?: RfiScheduleImpact | null;
  priority?: RfiPriority | null;
  discipline?: Array<RfiDiscipline>;
  category?: Array<RfiCategory>;
  reference?: string;
  customAttributes?: Array<CustomAttributeValue>;
  rfiTypeId: string;
  previousStatus?: RfiStatus;
  workflowType: WorkflowType;
  maxAssignees: number;
  officialResponseActors?: Assignee[];
  syncVersion?: string;
  bridgedSource?: boolean;
  bridgedTarget?: boolean;
  bridgeSyncOutdated?: boolean;
  permittedActions: Pick<RfiPermittedActions, 'nudge' | 'share' | 'updatePlacements'>;
}

/**
 *
 * @export
 * @interface Rfi
 */
export interface Rfi extends BaseRfi {
  permittedActions: RfiPermittedActions;
  responses: AssigneeResponse[];
  draftResponses: AssigneeResponse[];
}

export interface UpdateRfiResponse extends Rfi {
  attachments?: Array<Attachment>;
}

export type CreateFormSuggestionsResponse = Omit<
  BaseRfi,
  | 'id'
  | 'createdAt'
  | 'createdBy'
  | 'virtualFolderUrn'
  | 'status'
  | 'assignedTo'
  | 'managerId'
  | 'constructionManagerId'
  | 'architects'
  | 'reviewers'
  | 'dueDate'
  | 'attachmentsCount'
  | 'commentsCount'
  | 'officialResponse'
  | 'officialResponseStatus'
  | 'respondedAt'
  | 'respondedBy'
  | 'updatedBy'
  | 'updatedAt'
  | 'openedAt'
  | 'closedAt'
  | 'closedBy'
  | 'containerId'
  | 'projectId'
  | 'answeredAt'
  | 'answeredBy'
  | 'previousStatus'
  | 'maxAssignees'
  | 'officialResponseActors'
  | 'syncVersion'
  | 'bridgedSource'
  | 'bridgedTarget'
  | 'bridgeSyncOutdated'
  | 'permittedActions'
>;

/**
 * The custom identifier of the RFI (required in case that status is set to open).
 * @export
 * @type RfiCustomIdentifier
 */
export type RfiCustomIdentifier = string;

/**
 * The list of actions that are permitted for the user.
 * @export
 * @interface RfiPermittedActions
 */
export interface RfiPermittedActions {
  /**
   *
   * @type {RfiPermittedActionsUpdateRfi}
   * @memberof RfiPermittedActions
   */
  updateRfi?: RfiPermittedActionsUpdateRfi;
  /**
   * Whether current user can create a comment on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  createComment?: boolean;
  /**
   * Whether current user can create an attachment on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  createAttachment?: boolean;
  /**
   * Whether current user can link/unlink PCO on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  linkPCO?: boolean;

  createResponse?: boolean;
  createResponseOnBehalf?: boolean;
  /**
   * The list of entities (companies or roles) on whose behalf the user can
   * create a response. Empty array means the user cannot create a response
   * on behalf of any entity. ``user`` is intentionally excluded — to respond
   * on behalf of another user, use the ``onBehalf`` field instead.
   * @type {Array<{ id: OxygenId; type: AssigneeType.Company | AssigneeType.Role }>}
   * @memberof RfiPermittedActions
   */
  createResponseOnBehalfOfEntities?: Array<{
    id: OxygenId;
    type: AssigneeType.Company | AssigneeType.Role;
  }>;
  remainingReviewers?: Assignee[];
  share?: boolean;
  createDocumentReference?: boolean;
  removeDocumentReference?: boolean;
  nudge?: boolean;
  /**
   * Whether current user can update or delete the RFI's placement (pushpin) in its current status.
   * Always present (as `false`) when placements are disabled for the project.
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  updatePlacements?: boolean;
}

/**
 * Update RFI action
 * @export
 * @interface RfiPermittedActionsUpdateRfi
 */
export interface RfiPermittedActionsUpdateRfi {
  /**
   * List of statuses in which the user can update the RFI
   * @type {Array<PermittedRfiStatus>}
   * @memberof RfiPermittedActionsUpdateRfi
   */
  permittedStatuses: {
    wfUS: Array<PermittedRfiStatus>;
    wfEU: Array<PermittedRfiStatus>;
  };
  /**
   * List of attributes the user can update on the RFI when not changing the status
   * @type {Array<PermittedRfiAttribute>}
   * @memberof RfiPermittedActionsUpdateRfi
   */
  permittedAttributes?: Array<PermittedRfiAttribute>;
  useCustomAttributes?: boolean;
}

/**
 * Permitted RFI status
 * @export
 * @interface PermittedRfiStatus
 */
export interface PermittedRfiStatus {
  /**
   *
   * @type {RfiStatus}
   * @memberof PermittedRfiStatus
   */
  status: RfiStatus;

  /**
   * The list of attributes that are required when creating an RFI.
   * @type {Array<PermittedRfiAttribute>}
   * @memberof PermittedRfiStatus
   */
  requiredAttributes: Array<PermittedRfiAttribute>;

  /**
   * Additional list of optional attributes
   * @type {Array<PermittedRfiAttribute>}
   * @memberof PermittedRfiStatus
   */
  permittedAttributes: Array<PermittedRfiAttribute>;
  maxAssignees: number;
}

/**
 * Permitted RFI Attribute
 * @export
 * @interface PermittedRfiAttribute
 */
export interface PermittedRfiAttribute {
  /**
   * The name of the RFI Attribute.
   * @type {string}
   * @memberof PermittedRfiAttribute
   */
  name: string;
  /**
   * The list of possible values for the attribute.
   * @type {Array<PermittedRfiAttributeValues>}
   * @memberof PermittedRfiAttribute
   */
  values?: Array<PermittedRfiAttributeValues>;
}
/**
 *
 * @export
 * @interface PermittedRfiAttributeValues
 */
export interface PermittedRfiAttributeValues {
  /**
   * The value of the RFI attribute.
   * @type {string}
   * @memberof PermittedRfiAttributeValues
   */
  value: string;
  /**
   * The type of RFI attribute.
   * @type {string}
   * @memberof PermittedRfiAttributeValues
   */
  type?: string;
}

export type RfiLocationId = string;

export enum RfiFilterStatus {
  Draft = 'draft',
  Submitted = 'submitted',
  Open = 'open',
  Answered = 'answered',
  Closed = 'closed',
  Void = 'void',
  All = 'all',
}

export const RfiFieldOptions = [
  'id',
  'customIdentifier',
  'title',
  'question',
  'status',
  'previousStatus',
  'assignedTo',
  'dueDate',
  'locationDescription',
  'officialResponse',
  'respondedAt',
  'respondedBy',
  'createdBy',
  'createdAt',
  'updatedBy',
  'updatedAt',
  'openedAt',
  'closedAt',
  'closedBy',
  'containerId',
  'suggestedAnswer',
  'coReviewers',
  'optionalReviewers',
  'watchers',
  'answeredAt',
  'answeredBy',
  'costImpact',
  'scheduleImpact',
  'priority',
  'discipline',
  'category',
  'reference',
  'attachmentsCount',
  'commentsCount',
  'managerId',
  'constructionManagerId',
  'architects',
  'reviewers',
  'customAttributes',
  'locations',
  'officialResponseStatus',
  'rfiTypeId',
  'syncVersion',
  'bridgedSource',
  'bridgedTarget',
  'bridgeSyncOutdated',
] as const;

export type RfiFieldOption = (typeof RfiFieldOptions)[number];
